'use client';

import { useState } from 'react';
import { CareerPath } from '@/lib/careerPaths';
import styles from './SuccessStorySubmission.module.css';

interface SuccessStorySubmissionProps {
  career: CareerPath;
  userSegment?: string | null;
  onClose?: () => void;
}

interface FormData {
  name: string;
  email: string;
  previousRole: string;
  currentRole: string;
  transitionTime: string;
  biggestChallenge: string;
  keyToSuccess: string;
  advice: string;
  willingToConnect: boolean;
  photoConsent: boolean;
}

const INITIAL_FORM_DATA: FormData = {
  name: '',
  email: '',
  previousRole: '',
  currentRole: '',
  transitionTime: '',
  biggestChallenge: '',
  keyToSuccess: '',
  advice: '',
  willingToConnect: false,
  photoConsent: false,
};

export default function SuccessStorySubmission({
  career,
  userSegment = null,
  onClose,
}: SuccessStorySubmissionProps) {
  const [step, setStep] = useState<'intro' | 'form' | 'success'>('intro');
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateField = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/success-stories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          careerId: career.id,
          careerTitle: career.title,
          userSegment,
          submittedAt: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit story');
      }

      setStep('success');
    } catch {
      setError('Unable to submit your story. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const transitionTimeOptions = [
    'Less than 3 months',
    '3-6 months',
    '6-12 months',
    '1-2 years',
    'More than 2 years',
    'Still in transition',
  ];

  return (
    <div className={styles.container}>
      {/* Close Button */}
      {onClose && (
        <button className={styles.closeButton} onClick={onClose} aria-label="Close">
          ✕
        </button>
      )}

      {/* Intro Step */}
      {step === 'intro' && (
        <div className={styles.introSection}>
          <div className={styles.iconLarge}>🌟</div>
          <h3>Share Your Success Story</h3>
          <p className={styles.introText}>
            Have you successfully transitioned into a {career.title} role? Your story could inspire
            and guide others on the same path. Share your experience and help build a community
            of career changers.
          </p>

          <div className={styles.benefits}>
            <h4>Why Share Your Story?</h4>
            <ul>
              <li>
                <span className={styles.benefitIcon}>💪</span>
                <span>Inspire others facing the same challenges you overcame</span>
              </li>
              <li>
                <span className={styles.benefitIcon}>🤝</span>
                <span>Connect with others in the {career.title} community</span>
              </li>
              <li>
                <span className={styles.benefitIcon}>📣</span>
                <span>Get featured on our success stories page</span>
              </li>
              <li>
                <span className={styles.benefitIcon}>🎁</span>
                <span>Receive a thank-you gift (featured stories only)</span>
              </li>
            </ul>
          </div>

          <button className={styles.primaryButton} onClick={() => setStep('form')}>
            Share My Story
          </button>

          <p className={styles.disclaimer}>
            Submissions are reviewed before publishing. We respect your privacy and only share what you consent to.
          </p>
        </div>
      )}

      {/* Form Step */}
      {step === 'form' && (
        <form className={styles.formSection} onSubmit={handleSubmit}>
          <h3>Tell Us Your Story</h3>
          <p className={styles.formIntro}>
            Fill out the form below. All fields with * are required.
          </p>

          {error && <div className={styles.errorMessage}>{error}</div>}

          {/* Basic Info */}
          <div className={styles.formGroup}>
            <label htmlFor="name">Your Name *</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="Jane Smith"
              required
            />
            <span className={styles.fieldHint}>This will be displayed with your story</span>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder="jane@example.com"
              required
            />
            <span className={styles.fieldHint}>We&apos;ll only use this to contact you about your submission</span>
          </div>

          {/* Career Details */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="previousRole">Previous Role *</label>
              <input
                type="text"
                id="previousRole"
                value={formData.previousRole}
                onChange={(e) => updateField('previousRole', e.target.value)}
                placeholder="Retail Manager"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="currentRole">Current Role *</label>
              <input
                type="text"
                id="currentRole"
                value={formData.currentRole}
                onChange={(e) => updateField('currentRole', e.target.value)}
                placeholder={career.title}
                required
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="transitionTime">How long did your transition take? *</label>
            <select
              id="transitionTime"
              value={formData.transitionTime}
              onChange={(e) => updateField('transitionTime', e.target.value)}
              required
            >
              <option value="">Select an option</option>
              {transitionTimeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Story Questions */}
          <div className={styles.formGroup}>
            <label htmlFor="biggestChallenge">What was your biggest challenge? *</label>
            <textarea
              id="biggestChallenge"
              value={formData.biggestChallenge}
              onChange={(e) => updateField('biggestChallenge', e.target.value)}
              placeholder="What obstacles did you face during your transition?"
              rows={3}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="keyToSuccess">What was the key to your success? *</label>
            <textarea
              id="keyToSuccess"
              value={formData.keyToSuccess}
              onChange={(e) => updateField('keyToSuccess', e.target.value)}
              placeholder="What made the biggest difference in your transition?"
              rows={3}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="advice">What advice would you give to others? *</label>
            <textarea
              id="advice"
              value={formData.advice}
              onChange={(e) => updateField('advice', e.target.value)}
              placeholder="If you could tell someone just starting this journey one thing..."
              rows={3}
              required
            />
          </div>

          {/* Consent */}
          <div className={styles.consentSection}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.willingToConnect}
                onChange={(e) => updateField('willingToConnect', e.target.checked)}
              />
              <span>I&apos;m willing to connect with others making a similar transition</span>
            </label>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.photoConsent}
                onChange={(e) => updateField('photoConsent', e.target.checked)}
              />
              <span>I consent to PathFinder reaching out for a photo/video testimonial</span>
            </label>
          </div>

          {/* Actions */}
          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setStep('intro')}
            >
              ← Back
            </button>
            <button
              type="submit"
              className={styles.primaryButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit My Story'}
            </button>
          </div>
        </form>
      )}

      {/* Success Step */}
      {step === 'success' && (
        <div className={styles.successSection}>
          <div className={styles.successIcon}>🎉</div>
          <h3>Thank You for Sharing!</h3>
          <p className={styles.successText}>
            Your story has been submitted for review. If selected for publication, we&apos;ll
            reach out to you at <strong>{formData.email}</strong> within the next few days.
          </p>

          <div className={styles.successActions}>
            <p>What happens next:</p>
            <ol>
              <li>Our team reviews your submission (1-3 business days)</li>
              <li>If approved, we&apos;ll reach out to confirm details</li>
              <li>Your story gets published to inspire others!</li>
            </ol>
          </div>

          <div className={styles.shareSection}>
            <p>In the meantime, spread the word:</p>
            <div className={styles.shareButtons}>
              <button
                className={styles.shareButton}
                onClick={() => {
                  const text = `I just shared my career transition story with @PathFinder! From ${formData.previousRole} to ${formData.currentRole} 🚀`;
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
                }}
              >
                Share on Twitter
              </button>
              <button
                className={styles.shareButton}
                onClick={() => {
                  const text = `I successfully transitioned from ${formData.previousRole} to ${formData.currentRole}! If you're considering a career change, check out PathFinder.`;
                  window.open(`https://www.linkedin.com/shareArticle?mini=true&summary=${encodeURIComponent(text)}`, '_blank');
                }}
              >
                Share on LinkedIn
              </button>
            </div>
          </div>

          {onClose && (
            <button className={styles.closeLink} onClick={onClose}>
              Close this panel
            </button>
          )}
        </div>
      )}
    </div>
  );
}
